<template>
  <div v-if="company && offer">
    <div
      v-if="!mini"
      class="q-my-md text-h4 text-weight-bold text-center"
    >
      {{ offer.name }}
    </div>
    <QList>
      <QItem
        v-for="prop of computedMemoizedBuild"
        :key="prop.slug"
        dense
        class="q-px-none"
      >
        <QItemSection
          avatar
          top
          class="q-pa-none gt-xs"
        >
          <QAvatar
            :icon="prop.icon"
            size="lg"
            text-color="primary"
          />
        </QItemSection>
        <QItemSection>
          <div class="flex items-center q-gutter-xs">
            <div class="text-subtitle text-weight-bold">
              {{ $t({ id: prop.label }) }}&nbsp;:
            </div>
            <div v-if="prop.snippet === 'text' || (prop.snippet === 'tools' && $q.screen.lt.md)">
              {{ prop.snippet === 'tools' ? prop.value.join(', ') : prop.value }}
            </div>
          </div>
          <div v-if="!(prop.snippet === 'text' || (prop.snippet === 'tools' && $q.screen.lt.md))">
            <ul
              v-if="prop.snippet === 'list'"
              class="q-my-sm"
            >
              <li
                v-for="element of prop.value"
                :key="element"
              >
                {{ element }}
              </li>
            </ul>
            <ul
              v-else-if="prop.snippet === 'tools'"
              class="q-my-sm"
            >
              <template
                v-for="element of prop.value"
                :key="element"
              >
                <!-- CA tools, some formatting + link -->
                <li
                  v-if="toolsValues.find(t => t.label === element)"
                  class="cursor-pointer"
                  @click="openTool(toolsValues.find(t => t.label === element).value)"
                >
                  <span>{{ element }}</span>
                  <QIcon
                    class="text-primary q-ml-sm"
                    name="uil:external-link-alt"
                  />
                </li>
                <li v-else>
                  {{ element }}
                </li>
              </template>
            </ul>
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-else-if="prop.snippet === 'html'"
              class="text-body-2 text-weight-regular"
              v-html="prop.value"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </QItemSection>
      </QItem>
    </QList>
  </div>
</template>

<script setup>
// #region IMPORTS AND INIT
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { $web } from 'hc-core/utils/routing'
import { $t } from 'hc-core/composables/intl'
import { concat, compact, get, memoize } from 'lodash'
import { getLocationLabel } from 'hc-core/utils/formatting'
import { getCombinedAdvantages, getCombinedTools } from 'hc-core/utils/offer'
import { alphabeticalOrder } from 'hc-core/utils/array'
const store = useStore()
const router = useRouter()
// #endregion

// #region EMITS AND PROPS
const props = defineProps({
  company: {
    type: Object,
    default: null,
  },
  offer: {
    type: Object,
    default: null,
  },
  mini: {
    type: Boolean,
    default: false,
  },
})
// #endregion

// Categories
const categoriesById = computed(() => store.getters['common/categoriesById'])
const offerCategoriesIds = computed(() => store.getters['common/offerCategoriesIds'])
const contractCategories = computed(() => {
  const categories = []
  if (offerCategoriesIds.value && offerCategoriesIds.value.length) {
    for (const catId of offerCategoriesIds.value) {
      if (categoriesById.value[catId]) {
        categories.push({
          label: categoriesById.value[catId].name,
          value: categoriesById.value[catId].id,
          needDuration: ['ctgy_J9Ap2E1VZy1kszSVVZy', 'ctgy_zC01TE1E821lhRR9E82', 'ctgy_2X0J7E1yoK1lMDoxyoK', 'ctgy_SOyXrE1YW11m1pRBYW1', 'ctgy_HMYe0E1WG01ktfVPWG0'].includes(catId)
        })
      }
    }
  }
  return categories
})

// Tools
// toolsValues () { return this.getCustomAttributeValues('tools') },
function openTool (name) {
  if (process.env.HC_PLATFORM === 'WEB') {
    window.open(router.resolve({ name: 'knowledgeTools', params: { name } }).href, '_blank')
  } else {
    window.open($web({ name: 'knowledgeTools', params: { name } }), '_blank')
  }
}

// Main function
const generateBuild = memoize(({ company, offer, mini, contractCategories }) => {
  let build = []

  // Location
  const locationsNb =  get(offer, 'locations', []).length
  build.push({
    slug: 'location',
    label: 'offer.location',
    icon: 'uil:map-marker',
    snippet: locationsNb > 1 ? 'list' : 'text',
    value: locationsNb === 1 ? getLocationLabel(offer.locations[0]) : offer.locations.map(i => getLocationLabel(i))
  })

  // Category - Contract duration
  const offerCategory = contractCategories.find(c => c.value === offer.categoryId)
  build.push({
    slug: 'categoryId',
    label: 'offer.contractType',
    icon: 'uil:file-edit-alt',
    value: offer.categoryId
      ? `${get(offerCategory, 'label', null)}${get(offerCategory, 'needDuration', false)
        ? ` - ${get(offer, 'metadata._job.contractDuration', 'X')} mois`
        : ''}`
      : null
  })

  // Job schedule
  const jobSchedule = get(offer, 'customAttributes.jobSchedule')
  build.push({
    slug: 'rythm',
    label: 'offer.contractRythm',
    icon: 'uil:clock-three',
    value: jobSchedule ? $t(`customAttributes.jobSchedule.${jobSchedule}`) : null
  })

  // Remote
  const remoteWork = get(offer, 'customAttributes.remoteWork')
  build.push({
    slug: 'remote',
    label: 'offer.remoteWork',
    icon: 'uil:home',
    value: remoteWork ? $t(`customAttributes.remoteWork.${remoteWork}`) : null
  })

  // ContractDate
  const contractDate = get(offer, 'metadata._job.contractDate')
  build.push({
    slug: 'date',
    label: 'offer.contractDate',
    icon: 'uil:schedule',
    value: contractDate ? ['notSpecified', 'asap'].includes(contractDate) ? $t(`component.forms.offerForm.contractDate.${contractDate}`) : $t('time.date', { fmtd: contractDate }) : null
  })

  // Formation
  const formation = compact(concat(
    get(offer, 'customAttributes.formation', []).map(i => $t(`customAttributes.formation.${i}`)),
    get(offer, 'metadata._job.formation', [])
  ))
  build.push({
    slug: 'formation',
    label: 'offer.formation',
    icon: 'uil:graduation-cap',
    value: formation.length ? alphabeticalOrder(formation).join(', ') : null
  })

  // Experience
  const experience = compact(get(offer, 'customAttributes.experience', []).map(i => $t(`customAttributes.experience.${i}`)))
  build.push({
    slug: 'experience',
    label: 'offer.experience',
    icon: 'uil:hourglass',
    value: experience.length ? experience.join(', ') : null
  })

  // Tools
  const tools = getCombinedTools({ offer, company })
  build.push({
    slug: 'tools',
    label: 'offer.tools',
    icon: 'uil:wrench',
    snippet: 'list',
    value: tools.length ? tools : null
  })

  // Soft-skills
  const softSkills = compact(concat(
    get(offer, 'customAttributes.softSkills', []).map(i => $t(`customAttributes.softSkills.${i}`)),
    get(offer, 'metadata._job.softSkills', [])
  ))
  build.push({
    slug: 'soft-skills',
    label: 'offer.softSkills',
    icon: 'uil:smile-beam',
    value: softSkills.length ? alphabeticalOrder(softSkills).join(', ') : null
  })

  // Wage
  let wageValue = null
  if (get(offer, 'metadata._job.salaryUndefined', false)) {
    wageValue = $t('component.forms.offerForm.wage.undefined_label')
  } else if (get(offer, 'metadata._job.salaryType', null) && get(offer, 'customAttributes.salaryPeriod', null) && (get(offer, 'customAttributes.salaryMin', false) || get(offer, 'customAttributes.salaryMax', false))) {
    wageValue = $t('component.forms.offerForm.wage.offerString', {
      tax: get(offer, 'metadata._job.salaryTax'),
      type: get(offer, 'metadata._job.salaryType'),
      min: get(offer, 'customAttributes.salaryMin'),
      max: get(offer, 'customAttributes.salaryMax'),
      period: get(offer, 'customAttributes.salaryPeriod'),
    })
  }
  build.push({
    slug: 'wage',
    label: 'offer.wage',
    icon: 'uil:euro-circle',
    value: wageValue
  })

  // Advantages
  const advantages = getCombinedAdvantages({ offer, company })
  build.push({
    slug: 'advantages',
    label: 'offer.advantages',
    icon: 'uil:award',
    snippet: 'list',
    value: advantages.length ? advantages : null
  })

  // Description
  const offerDescription = get(offer, 'description')
  const companyDescription = get(company, 'description')
  build.push({
    slug: 'description',
    label: 'offer.description',
    icon: 'uil:clipboard-notes',
    snippet: 'html',
    value: '<span><strong>À propos du poste</strong></span><br>'
      .concat(offerDescription && offerDescription.length ? offerDescription : $t('offer.description_unfilled'))
      .concat(companyDescription ? `<hr><br><span><strong>À propos de ${company.name}</strong></span><br>${companyDescription}` : '')
  })

  if (mini) build = build.filter(i => ['location', 'contract', 'wage'].includes(i.slug))
  build.forEach(i => { i.snippet = i.snippet ?? 'text' })
  return build.filter(i => !!i.value)
})
const computedMemoizedBuild = generateBuild({
  ...props,
  contractCategories: contractCategories.value
})
</script>
